import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQStatisticCard from '../../components/statistic-card';
import UQSpacer from '../../components/spacer';
import UQCode from '../../components/code';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import UQCard from '../../components/card';
import UQAccordion from '../../components/accordion';
import AccordionItem from '../../components/accordion/accordion-item';
import UQGrid from '../../components/grid';
import { BREADCRUMB_ROOT } from '../../common/config';

const attributes = [
  {
    attribute: 'label',
    type: 'string',
    default: "''",
    description: 'The label displayed above the value.',
  },
  {
    attribute: 'value',
    type: 'React.ReactNode',
    default: "''",
    description: 'The main value to display (large).',
  },
  {
    attribute: 'size',
    type: "'s' | 'm' | 'l'",
    default: "'m'",
    description: 'Controls the size of the value text.',
  },
  {
    attribute: 'className',
    type: 'string',
    default: "''",
    description: 'Additional class names for custom styling.',
  },
  {
    attribute: 'style',
    type: 'React.CSSProperties',
    default: 'undefined',
    description: 'Inline styles for the card container.',
  },
];

const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
  { columnId: 'attribute', renderHeaderCell: () => 'Attribute', renderCell: (item) => item.attribute },
  { columnId: 'type', renderHeaderCell: () => 'Type', renderCell: (item) => <code>{item.type}</code> },
  { columnId: 'default', renderHeaderCell: () => 'Default', renderCell: (item) => <code>{item.default}</code> },
  { columnId: 'description', renderHeaderCell: () => 'Description', renderCell: (item) => item.description },
];

const codeExample = `
import UQStatisticCard from '@uniqconsulting/ui/components/statistic-card';

<UQStatisticCard label="Total" value={523} size="l" />
<UQStatisticCard label="Shared" value={0} size="m" />
<UQStatisticCard label="Private" value={37} size="s" />
`;

const cardExample = `
import UQCard from '@uniqconsulting/ui/components/card';
import UQStatisticCard from '@uniqconsulting/ui/components/statistic-card';

<UQCard header="Statistics">
  <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-end' }}>
    <UQStatisticCard label="Total" value={523} size="l" />
    <UQStatisticCard label="Shared" value={0} size="m" />
    <UQStatisticCard label="Private" value={37} size="s" />
  </div>
</UQCard>
`;

const accordionExample = `
import UQAccordion from '@uniqconsulting/ui/components/accordion';
import AccordionItem from '@uniqconsulting/ui/components/accordion/accordion-item';
import UQStatisticCard from '@uniqconsulting/ui/components/statistic-card';

<UQAccordion singleExpand>
  <AccordionItem title="Statistics">
    <UQStatisticCard label="Total" value={523} size="l" />
  </AccordionItem>
</UQAccordion>
`;

const StatisticCardDemoPage = () => (
  <>
    <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Statistic Card']} />
    <h1>UQStatisticCard Demo</h1>
    <UQSpacer size="m" />

    {/* Description */}
    <h2>Description</h2>
    <p>
      <code>UQStatisticCard</code> displays a small label and a large value, ideal for dashboards and statistic overviews. It has no border or background by default, so it can be used flexibly inside grid or card containers.
    </p>
    <UQSpacer size="m" />

    {/* Attributes Table */}
    <h2>Attributes</h2>
    <UQSpacer size="s" />
    <UQDataGrid
      items={attributes}
      columns={attributeColumns}
      sortable={false}
      selectionMode="none"
      rowLines
    />
    <UQSpacer size="m" />

    {/* Size Variations */}
    <h2>Size Variations</h2>
    <UQSpacer size="s" />
    <UQGrid justify="space-around">
      <UQStatisticCard label="Total" value={523} size="l" />
      <UQStatisticCard label="Shared" value={0} size="m" />
      <UQStatisticCard label="Private" value={37} size="s" />
    </UQGrid>
    <UQSpacer size="m" />
    <UQCode code={codeExample.trim()} enableCopy />
    <UQSpacer size="m" />

    {/* In a Card */}
    <h2>In a Card</h2>
    <UQSpacer size="s" />
    <UQCard header="Statistics">
      <UQGrid justify="space-around">
        <UQStatisticCard label="Total" value={523} size="l" />
        <UQStatisticCard label="Shared" value={0} size="m" />
        <UQStatisticCard label="Private" value={37} size="s" />
      </UQGrid>
    </UQCard>
    <UQSpacer size="s" />
    <UQCode code={cardExample.trim()} enableCopy />
    <UQSpacer size="m" />

    {/* In a Single Accordion */}
    <h2>In a Single Accordion</h2>
    <UQSpacer size="s" />
    <UQAccordion singleExpand>
      <AccordionItem title="Statistics">
        <UQStatisticCard label="Total" value={523} size="l" />
      </AccordionItem>
    </UQAccordion>
    <UQSpacer size="s" />
    <UQCode code={accordionExample.trim()} enableCopy />
  </>
);

export default StatisticCardDemoPage;